import {
  createFhirDate,
  HumanNamePrefix,
  PersonIdentityCode,
} from '@quromedical/fhir-common';
import { isValid } from 'date-fns';

import type {
  MedicalAidPerson,
  PatientDetail,
  PersonGender,
} from '../interface';

/**
 * Decodes common HTML entities that may appear in patient names from medical aid systems
 * @param text - The text that may contain HTML entities
 * @returns The text with HTML entities decoded
 */
const decodeHtmlEntities = (text: string): string => {
  if (!text) return text;

  const entities: Record<string, string> = {
    '&apos;': "'",
    '&quot;': '"',
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
  };

  return text.replace(
    /&[a-z]+;/gi,
    (match) => entities[match.toLowerCase()] || match,
  );
};

const humanPrefixes: Record<string, HumanNamePrefix> = {
  mr: HumanNamePrefix.mr,
  mrs: HumanNamePrefix.mrs,
  miss: HumanNamePrefix.miss,
  dr: HumanNamePrefix.dr,
  mnr: HumanNamePrefix.mr,
  mev: HumanNamePrefix.mrs,
  mej: HumanNamePrefix.mrs,
};

export const mapTitleToHumanNamePrefix = (
  title = '',
): HumanNamePrefix | undefined => {
  const prefix = humanPrefixes[title.toLowerCase()];
  return prefix;
};

const genders: Record<string, PersonGender> = {
  male: 'male',
  female: 'female',
};

export const mapGenderToPersonGender = (gender = 'unknown'): PersonGender => {
  const prefix = genders[gender.toLowerCase()];
  return prefix || 'unknown';
};

const isEligibleAsNextOfKin = (patient: PatientDetail): boolean =>
  patient.dependencyType !== 'Child';

export const isMainMember = (patient: PatientDetail): boolean =>
  patient.dependencyType === 'Principle party';

/**
 * Convert a date to the fhir format and ensure that it is valid
 * @returns a fhir date if the date given is valid
 */
const convertDate = (date?: string): string | undefined => {
  if (!date) {
    return undefined;
  }

  const parsedDate = new Date(date);

  const isDateValid = isValid(parsedDate);

  if (!isDateValid) {
    return undefined;
  }

  return createFhirDate(parsedDate);
};

const convertIdentifier = (id?: string): string | undefined => {
  if (!id) {
    return undefined;
  }

  // TODO: use a more robust id validation, for now this is to eliminate the "no record" datapoints
  if (id.toLowerCase().includes('no record')) {
    return undefined;
  }

  return id;
};

const isPatientActiveMedicalAidMember = (patient: PatientDetail): boolean =>
  patient.status.toLocaleLowerCase() === 'the membership is valid and active';

export const mapHealthbridgePatientToPerson = (
  schemeCode: string,
  patient: PatientDetail,
): Partial<MedicalAidPerson> => ({
  general: {
    birthDate: convertDate(patient.dateOfBirth),
    gender: mapGenderToPersonGender(patient.gender),
    identifierValue: convertIdentifier(patient.idNumber),
    identifierCode: PersonIdentityCode.saIdNumber,
    givenName: decodeHtmlEntities(patient.firstName),
    familyName: decodeHtmlEntities(patient.lastName),
    prefixName: mapTitleToHumanNamePrefix(patient.title),
  },
  medicalAidMembership: {
    medicalAid: {
      schemeCode,
      schemeName: patient.schemeName,
      plan: patient.plan,
      membershipNumber: patient.membershipNumber,
    },
    dependantCode: patient.dependantCode,
    start: convertDate(patient.membershipEffectiveDateFrom),
    end: convertDate(patient.membershipToDate),
    status: patient.status,
    isActive: isPatientActiveMedicalAidMember(patient),
    isEligibleAsNextOfKin: isEligibleAsNextOfKin(patient),
    isMainMember: isMainMember(patient),
  },
});
